import { GQLPromoImageFragment } from '@theprintbar/graphql';
import { ImageSize } from '@theprintbar/shared';
import clsx from 'clsx';
import { HTMLAttributes } from 'react';
import { ButtonSize, OutlineButton, OutlineLinkButton } from '../Button';

export interface PromoImageContainerProps extends HTMLAttributes<HTMLDivElement> {
	promoImage?: GQLPromoImageFragment | null;
	imageClass?: string;
	lazyLoad?: boolean;
	promoPosition?: 'left' | 'right' | 'none';
	stickers?: {
		position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
		image: string | undefined;
		className?: string;
	}[];
	size?: ImageSize;
}

export function PromoImageContainer({
	promoImage,
	imageClass,
	lazyLoad = true,
	promoPosition = 'right',
	className,
	stickers,
	size = ImageSize.Large,
	...divProps
}: PromoImageContainerProps) {
	if (!promoImage) return null;

	const { image, credit, creditUrl, altText, focalPointX, focalPointY } = promoImage;

	const imagePath = new URL(image?.url || '');
	const imagePathString = imagePath.toString();
	const separator = imagePathString.includes('?') ? '&' : '?';
	if (!!size) {
		imagePath.searchParams.set('size', `${size}`);
	}

	//Grab the current image size, and generate a srcset for the next 2 smallest sizes at most
	//based on the size prop
	function generateSrcSet(baseUrl: string): string {
		const idx = Object.values(ImageSize).sort().indexOf(size);
		if (idx === -1) return '';

		// Always include the target size + up to 2 smaller ones
		const candidates = Object.values(ImageSize)
			.sort()
			.slice(Math.max(0, idx - 2), idx + 1);

		return candidates.map(w => `${baseUrl}?size=${w} ${w}w`).join(', ');
	}

	return (
		<div className={clsx('relative', className)} {...divProps}>
			<img
				loading={lazyLoad ? 'lazy' : 'eager'}
				{...{
					fetchpriority: lazyLoad ? 'auto' : 'high',
				}}
				decoding="async"
				src={imagePath.toString()}
				srcSet={generateSrcSet(imagePathString)}
				alt={altText || ''}
				className={clsx('absolute object-cover h-full w-full', imageClass)}
				style={{ objectPosition: `${focalPointX || 50}% ${focalPointY || 50}%` }}
				sizes="(min-width: 1280px) 33vw, (min-width: 1024px) 50vw, 95vw"
			/>
			{!!credit && promoPosition !== 'none' && (
				<div className={clsx('absolute bottom-5 font-normal text-xs', promoPosition === 'left' ? 'left-5' : 'right-5')}>
					{creditUrl ? (
						<OutlineLinkButton to={creditUrl} size={ButtonSize.XS} className="bg-white-pearl underline border-0 text-grey">
							<h6>{credit}</h6>
						</OutlineLinkButton>
					) : (
						<OutlineButton size={ButtonSize.XS} className="bg-white-pearl border-0 text-grey underline">
							<h6>{credit}</h6>
						</OutlineButton>
					)}
				</div>
			)}
			{stickers?.map(sticker => (
				<img
					loading="lazy"
					src={sticker.image}
					className={clsx(
						'hidden md:block absolute lg:h-32 lg:w-32 h-16 w-16 rounded-full',
						sticker.position === 'top-left' && 'left-8 -top-8 lg:-top-14 lg:left-16',
						sticker.position === 'top-right' && 'right-8  -top-8 lg:-top-14 lg:right-16',
						sticker.position === 'bottom-left' && 'left-8 -bottom-8 lg:-bottom-14 lg:left-16',
						sticker.position === 'bottom-right' && 'right-8 -bottom-8 lg:-bottom-14 lg:right-16 ',
						sticker.className,
					)}
				/>
			))}
		</div>
	);
}
